/**
 * Catering pricing — single source of truth shared by the client UI and the
 * server payment endpoints. Keeping ONE implementation guarantees the amount the
 * client displays equals the amount the server charges (no drift). The server
 * recomputes from DB-sourced prices (never trusting client-sent amounts).
 *
 * Mirrors the original client logic in catering-order.tsx (calculateTotalPrice /
 * calculateFinalPricing). Each selection's `price` is the menu item's price.
 */

export interface CateringSelectionLite {
  categoryName: string;
  price: string | number;
}

// Categories that have bespoke pricing rules below. Everything else is billed
// as (item price × guests). MUST match catering-order.tsx's exclusion list.
const SPECIAL_CATEGORIES = new Set<string>([
  'juices',
  'starters',
  'breads',
  'gravies',
  'rice_dishes',
  'curry_for_rice',
  'non_veg_items',
  'veg_counter',
  'desserts',
  'live_counters',
  'settings_staff',
]);

function num(p: string | number | null | undefined): number {
  const n = typeof p === 'number' ? p : parseFloat(String(p ?? '0'));
  return isNaN(n) ? 0 : n;
}

/**
 * Per-person menu subtotal × guest count. Excludes the package base price
 * (package price is intentionally not charged — only menu items).
 */
export function computeCateringSubtotal(params: {
  selections: CateringSelectionLite[];
  mocktailCombosCount: number;
  guestCount: number;
}): number {
  const { selections, mocktailCombosCount, guestCount } = params;
  let additionalCost = 0;

  // Fresh juice: ₹25 base for the category (up to 4 selections)
  if (selections.some((s) => s.categoryName === 'juices')) {
    additionalCost += 25 * guestCount;
  }

  // Mocktail combinations: ₹15 each
  additionalCost += mocktailCombosCount * 15 * guestCount;

  // Starters: ₹40 base if any base (₹0) starter selected, plus premium add-ons
  const starters = selections.filter((s) => s.categoryName === 'starters');
  if (starters.length > 0) {
    if (starters.some((s) => num(s.price) === 0)) {
      additionalCost += 40 * guestCount;
    }
    starters
      .filter((s) => num(s.price) > 0)
      .forEach((s) => {
        additionalCost += num(s.price) * guestCount;
      });
  }

  // Breads: ₹20 base + premium bread add-ons
  const breads = selections.filter((s) => s.categoryName === 'breads');
  if (breads.length > 0) {
    additionalCost += 20 * guestCount;
    breads.forEach((s) => {
      if (num(s.price) > 0) additionalCost += num(s.price) * guestCount;
    });
  }

  // Per-item categories with no base price
  ['gravies', 'rice_dishes', 'curry_for_rice', 'non_veg_items', 'live_counters'].forEach((cat) => {
    selections
      .filter((s) => s.categoryName === cat)
      .forEach((s) => {
        additionalCost += num(s.price) * guestCount;
      });
  });

  // Veg counter: ₹30 base (mandatory single selection)
  if (selections.some((s) => s.categoryName === 'veg_counter')) {
    additionalCost += 30 * guestCount;
  }

  // Desserts: ₹25 base + individual item pricing
  const desserts = selections.filter((s) => s.categoryName === 'desserts');
  if (desserts.length > 0) {
    additionalCost += 25 * guestCount;
    desserts.forEach((s) => {
      additionalCost += num(s.price) * guestCount;
    });
  }

  // Settings/staff: ₹30 base (auto-selected mandatory)
  if (selections.some((s) => s.categoryName === 'settings_staff')) {
    additionalCost += 30 * guestCount;
  }

  // All other categories: item price × guests
  selections
    .filter((s) => !SPECIAL_CATEGORIES.has(s.categoryName))
    .forEach((s) => {
      additionalCost += num(s.price) * guestCount;
    });

  return additionalCost;
}

export interface CateringPricing {
  subtotal: number;
  discount: number;
  discountRate: number;
  setupFee: number;
  gstAmount: number;
  totalAmount: number;
  advanceAmount: number;
  remainingAmount: number;
}

/** Guest-count discount tiers (mirrors client). */
export function getCateringDiscountRate(guests: number): number {
  if (guests >= 1000) return 0.1;
  if (guests >= 800) return 0.085;
  if (guests >= 600) return 0.07;
  if (guests >= 500) return 0.05;
  if (guests >= 400) return 0.03;
  return 0;
}

/** Applies discount, setup fee, 5% GST, and the 20% advance. */
export function computeCateringFinalPricing(subtotal: number, guestCount: number): CateringPricing {
  const gstRate = 0.05;
  const setupFee = guestCount < 400 && guestCount > 1 ? 5000 : 0;
  const discountRate = getCateringDiscountRate(guestCount);
  const discount = subtotal * discountRate;
  const afterDiscount = subtotal - discount + setupFee;
  const gstAmount = afterDiscount * gstRate;
  const totalAmount = afterDiscount + gstAmount;
  const advanceAmount = Math.round(totalAmount * 0.2);
  const remainingAmount = totalAmount - advanceAmount;

  return {
    subtotal,
    discount,
    discountRate,
    setupFee,
    gstAmount,
    totalAmount,
    advanceAmount,
    remainingAmount,
  };
}
